import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Edit2, Trash2, Plus, X, Pill } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/config/api";

export default function MedicalAidsPage() {
  const [medicalAids, setMedicalAids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({ medical_aid_name: "", plans: [] });
  const [newPlanInput, setNewPlanInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const planInputRef = useRef(null);

  useEffect(() => { fetchMedicalAids(); }, []);

  const fetchMedicalAids = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/medical-aids`);
      setMedicalAids(res.data || []);
    } catch {
      toast.error("Failed to load medical aids");
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setFormData({ medical_aid_name: "", plans: [] });
    setNewPlanInput("");
    setShowAddDialog(true);
  };

  const handleEditClick = (aid) => {
    setFormData({ medical_aid_name: aid.medical_aid_name, plans: [...aid.plans] });
    setNewPlanInput("");
    setEditingId(aid.id);
    setShowEditDialog(true);
  };

  const handleAddPlan = () => {
    const plan = newPlanInput.trim();
    if (!plan) return;
    if (formData.plans.includes(plan)) { toast.error("Plan already exists"); return; }
    setFormData({ ...formData, plans: [...formData.plans, plan] });
    setNewPlanInput("");
  };

  const handleRemovePlan = (index) => {
    setFormData({ ...formData, plans: formData.plans.filter((_, i) => i !== index) });
  };

  const handleSubmitAdd = async () => {
    if (!formData.medical_aid_name.trim()) { toast.error("Medical Aid name is required"); return; }
    try {
      await axios.post(`${API}/admin/medical-aids`, {
        medical_aid_name: formData.medical_aid_name.trim(),
        plans: formData.plans
      });
      toast.success("Medical Aid created");
      setShowAddDialog(false);
      fetchMedicalAids();
    } catch (err) {
      toast.error(err.response?.status === 409 ? "Medical Aid already exists" : "Failed to create");
    }
  };

  const handleSubmitEdit = async () => {
    if (!formData.medical_aid_name.trim()) { toast.error("Medical Aid name is required"); return; }
    try {
      await axios.put(`${API}/admin/medical-aids/${editingId}`, {
        medical_aid_name: formData.medical_aid_name.trim(),
        plans: formData.plans
      });
      toast.success("Medical Aid updated");
      setShowEditDialog(false);
      setEditingId(null);
      fetchMedicalAids();
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDeleteClick = (aid) => { setDeleteTarget(aid); setShowDeleteConfirm(true); };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/medical-aids/${deleteTarget.id}`);
      toast.success("Medical Aid deleted");
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      fetchMedicalAids();
    } catch {
      toast.error("Failed to delete");
    }
  };

  useEffect(() => {
    if (showAddDialog || showEditDialog) {
      const t = setTimeout(() => planInputRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [showAddDialog, showEditDialog]);

  const filteredAids = medicalAids.filter(a =>
    a.medical_aid_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Plan form section (shared between Add and Edit dialogs).
  // Kept as a JSX value, not a nested component: a nested component type is
  // recreated on every render, which remounts the input and drops focus.
  const planFormSection = (
    <>
      <div>
        <Label>Add Plan</Label>
        <div className="flex gap-2">
          <Input
            ref={planInputRef}
            value={newPlanInput}
            onChange={(e) => setNewPlanInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddPlan())}
            autoComplete="off"
            placeholder="e.g., Traditional Plan"
            data-testid="plan-name-input"
          />
          <Button onClick={handleAddPlan} variant="outline" size="icon" data-testid="add-plan-button">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {formData.plans.length > 0 && (
        <div>
          <Label className="text-xs text-muted-foreground">Plans ({formData.plans.length})</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.plans.map((plan, idx) => (
              <Badge key={idx} variant="secondary" className="pr-1 gap-1">
                {plan}
                <button onClick={() => handleRemovePlan(idx)} className="ml-1 hover:text-destructive">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}
    </>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="medical-aids-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Medical Aids
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Manage medical aid schemes and their plans</p>
        </div>
        <Button onClick={handleAddClick} className="gap-2" data-testid="add-medical-aid-button">
          <Plus className="w-4 h-4" /> Add Medical Aid
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-3">
          <Input
            placeholder="Search medical aids..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9"
            data-testid="medical-aids-search"
          />
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="flex gap-3">
        <Card className="flex-1">
          <CardContent className="p-3 flex items-center gap-2">
            <Pill className="w-5 h-5 text-primary" />
            <div>
              <p className="text-xl font-bold">{medicalAids.length}</p>
              <p className="text-xs text-muted-foreground">Medical Aids</p>
            </div>
          </CardContent>
        </Card>
        <Card className="flex-1">
          <CardContent className="p-3 flex items-center gap-2">
            <div>
              <p className="text-xl font-bold">{medicalAids.reduce((sum, a) => sum + (a.plans?.length || 0), 0)}</p>
              <p className="text-xs text-muted-foreground">Total Plans</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* List */}
      <div className="grid gap-3">
        {filteredAids.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">
                {searchQuery ? "No medical aids match your search" : "No medical aids yet. Click Add Medical Aid to get started."}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredAids.map((aid) => (
            <Card key={aid.id} data-testid={`medical-aid-card-${aid.id}`}>
              <CardHeader className="p-4 pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{aid.medical_aid_name}</CardTitle>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditClick(aid)} data-testid={`edit-medical-aid-${aid.id}`}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDeleteClick(aid)} data-testid={`delete-medical-aid-${aid.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {aid.plans?.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {aid.plans.map((plan, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">{plan}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No plans added</p>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Medical Aid</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Medical Aid Name *</Label>
              <Input
                value={formData.medical_aid_name}
                onChange={(e) => setFormData({ ...formData, medical_aid_name: e.target.value })}
                placeholder="e.g., Bankmed"
                data-testid="medical-aid-name-input"
              />
            </div>
            {planFormSection}
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={handleSubmitAdd} data-testid="submit-medical-aid">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Medical Aid</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Medical Aid Name *</Label>
              <Input
                value={formData.medical_aid_name}
                onChange={(e) => setFormData({ ...formData, medical_aid_name: e.target.value })}
                placeholder="e.g., Bankmed"
                data-testid="edit-medical-aid-name-input"
              />
            </div>
            {planFormSection}
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={handleSubmitEdit} data-testid="save-medical-aid">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete Medical Aid?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete <strong>{deleteTarget?.medical_aid_name}</strong>? This cannot be undone.
          </p>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button variant="destructive" onClick={handleConfirmDelete} data-testid="confirm-delete-medical-aid">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
